/* eslint-disable react/prop-types */
import {
  Button,
  Col,
  DatePicker,
  Form,
  Menu,
  Modal,
  Progress,
  Row,
  Spin,
  notification,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  descargarPlantillaSBC,
  deleteHistoricoSBCEmpleado,
  getHistoricoSBCEmpleadoVistaOptimizada,
  importarSBCEmpleado,
  patchHistoricoSBCEmpleado,
  postHistoricoSBCEmpleado,
} from 'api/estructuraOrganizacional/historicoSBCEmpleado';
import { getEmpleadosvistaOptimized } from 'api/estructuraOrganizacional/empleados';
import getProcesoEnCursoTerminado from 'api/catalogos/ProcesoEnCursoTerminado';
import { checkToken } from 'api/utils';
import CurrencyInput from 'components/CurrencyInput';
import Dragger from 'components/Dragger';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import currencyFormatter from 'utils/currencyFormatter';
import exportarExcelDesdeColumnas from 'utils/exportarExcelDesdeColumnas';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO, ESTATUS_CANCELADO } from 'utils/constants';

export const historicosbcempleadoPermission = {
  permissionModel: 'historicosbcempleado',
  catalogsModels: [],
  tabsModels: [],
};

const DESCRIPCION_PROCESO_IMPORTAR = 'Importar SBC Empleado';

const HistoricoSBCEmpleado = ({ permission }) => {
  const [form] = Form.useForm();
  const [formHistorial] = Form.useForm();
  const [formCarga] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [exportandoTodos, setExportandoTodos] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [data, setData] = useState([]);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleFormModal, setVisibleFormModal] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [visibleConfirmAutorizar, setVisibleConfirmAutorizar] = useState(false);
  const [visibleConfirmCancelar, setVisibleConfirmCancelar] = useState(false);
  const [visibleConfirmReactivar, setVisibleConfirmReactivar] = useState(false);

  // ── Polling ───────────────────────────────────────────────────────────────
  const [isPollingImport, setIsPollingImport] = useState(false);
  const [progresoImport, setProgresoImport] = useState({ en_curso: 0, total: 0 });
  const pollingImportRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const _empleados = await getEmpleadosvistaOptimized();
      if (mounted) {
        setEmpleados(_empleados);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const reload = async () => {
    const empId = form.getFieldValue('empleado');
    if (!empId) return;
    const raw = await getHistoricoSBCEmpleadoVistaOptimizada({ empleado_id: empId });
    const list = raw?.results || raw || [];
    setData(formatReceived(list));
  };

  // Polling useEffect — cada 2s hasta que procesando=false o máx ~11 min
  useEffect(() => {
    if (!isPollingImport) return () => {};

    let intentos = 0;
    const maxIntentos = 340;

    pollingImportRef.current = setInterval(async () => {
      try {
        intentos += 1;
        const lista = await getProcesoEnCursoTerminado({
          descripcion: DESCRIPCION_PROCESO_IMPORTAR,
        });
        const proceso = lista[0];

        if (proceso) {
          setProgresoImport({
            en_curso: proceso.registro_en_curso || 0,
            total: proceso.total_registros || 0,
          });
        }

        const termino = proceso && proceso.procesando === false;
        if (termino || intentos >= maxIntentos) {
          clearInterval(pollingImportRef.current);
          pollingImportRef.current = null;
          setIsPollingImport(false);
          setProgresoImport({ en_curso: 0, total: 0 });

          if (proceso?.resultado) {
            const byteCharacters = atob(proceso.resultado);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
              byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'SBC_Empleados_Resultado.xlsx';
            link.click();
            URL.revokeObjectURL(url);
          }

          notification.success({
            message: 'Importación completada',
            description: 'La carga masiva de SBC finalizó. Se descargó el archivo de resultado.',
            placement: 'topRight',
            duration: 6,
          });
          await reload();
        }
      } catch (_err) {
        // No se interrumpe el polling: el siguiente tick reintenta. Si el
        // error es por sesión expirada, utils/api.js ya avisó y cerró
        // sesión; aquí solo dejamos rastro para diagnóstico.
        // eslint-disable-next-line no-console
        console.error('[polling importación SBC de empleados] error al consultar estado', _err);
      }
    }, 2000);

    return () => {
      if (pollingImportRef.current) {
        clearInterval(pollingImportRef.current);
        pollingImportRef.current = null;
      }
    };
  }, [isPollingImport]);

  const onChangeEmpleado = async (empId) => {
    setSelectedKeys([]);
    setSelectedRecord(null);
    if (empId) {
      const raw = await getHistoricoSBCEmpleadoVistaOptimizada({ empleado_id: empId });
      const list = raw?.results || raw || [];
      setData(formatReceived(list));
    } else {
      setData([]);
    }
  };

  // ── Import Excel ──────────────────────────────────────────────────────────
  const onClickPlantilla = async () => {
    try {
      setLoading(true);
      await descargarPlantillaSBC();
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    formCarga.resetFields();
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formCarga.resetFields();
  };

  const menuImport = (
    <Menu
      items={[
        {
          key: 1,
          label: 'Obtener Plantilla',
          icon: <FileExcelOutlined />,
          onClick: onClickPlantilla,
        },
        {
          key: 2,
          label: 'Cargar',
          icon: <DownloadOutlined />,
          onClick: openModal,
        },
      ]}
    />
  );

  const onUpload = async () => {
    try {
      await formCarga.validateFields();
      const { file } = formCarga.getFieldsValue();
      if (file) {
        setLoading(true);
        const response = await importarSBCEmpleado(file.file);
        if (response?.status === 202) {
          setVisibleModal(false);
          formCarga.resetFields();
          setProgresoImport({ en_curso: 0, total: 0 });
          setIsPollingImport(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setSelectedRecord(null);
    formHistorial.resetFields();
    setVisibleFormModal(true);
  };

  const handleEdit = () => {
    if (!selectedRecord) return;
    formHistorial.setFieldsValue({
      salario_base_cotizacion_imss: selectedRecord.salario_base_cotizacion_imss,
      fecha_inicial: selectedRecord.fecha_inicial
        ? moment(selectedRecord.fecha_inicial) : null,
      fecha_final: selectedRecord.fecha_final
        ? moment(selectedRecord.fecha_final) : null,
    });
    setVisibleFormModal(true);
  };

  const onFinishHistorial = async () => {
    try {
      await formHistorial.validateFields();
    } catch {
      return;
    }
    const empId = form.getFieldValue('empleado');
    if (!empId) return;
    const values = formHistorial.getFieldsValue();
    const payload = {
      empleado: empId,
      salario_base_cotizacion_imss: values.salario_base_cotizacion_imss,
      fecha_inicial: values.fecha_inicial?.format('YYYY-MM-DD'),
      fecha_final: values.fecha_final?.format('YYYY-MM-DD') || null,
    };
    if (selectedRecord) {
      // Sin esto, el serializer (CustomModelSerializerMixin.validate) resetea
      // estados_globales a Activo cuando el campo no viene en el payload.
      payload.estados_globales = selectedRecord.estados_globales_id;
    }
    setLoading(true);
    const ok = selectedRecord
      ? await patchHistoricoSBCEmpleado(selectedRecord.id, payload)
      : await postHistoricoSBCEmpleado(payload);
    if (ok) {
      setVisibleFormModal(false);
      formHistorial.resetFields();
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  const confirmDelete = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    const ok = await deleteHistoricoSBCEmpleado(selectedRecord.id);
    if (ok) {
      setVisibleDelete(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  // ── Autorizar / Cancelar ──────────────────────────────────────────────────
  const onConfirmAutorizar = async () => {
    setLoading(true);
    const ok = await patchHistoricoSBCEmpleado(
      selectedRecord.id,
      { estados_globales: ESTATUS_AUTORIZADO },
    );
    if (ok) {
      setVisibleConfirmAutorizar(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  const onConfirmCancelar = async () => {
    setLoading(true);
    const ok = await patchHistoricoSBCEmpleado(
      selectedRecord.id,
      { estados_globales: ESTATUS_CANCELADO },
    );
    if (ok) {
      setVisibleConfirmCancelar(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  const onConfirmReactivar = async () => {
    setLoading(true);
    const ok = await patchHistoricoSBCEmpleado(
      selectedRecord.id,
      { estados_globales: ESTATUS_ACTIVO },
    );
    if (ok) {
      setVisibleConfirmReactivar(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  // ── Columnas ──────────────────────────────────────────────────────────────
  const columns = [
    {
      titleText: 'No. Empleado',
      dataIndex: 'empleado_numero',
      key: 'empleado_numero',
      width: 150,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'empleado_nombre',
      key: 'empleado_nombre',
      width: 300,
    },
    {
      titleText: 'SBC IMSS',
      dataIndex: 'salario_base_cotizacion_imss',
      key: 'salario_base_cotizacion_imss',
      align: 'right',
      width: 160,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : 'Vigente'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales_descripcion',
      key: 'estados_globales_descripcion',
      width: 130,
    },
  ];

  const onClickExportarTodos = async () => {
    setExportandoTodos(true);
    const raw = await getHistoricoSBCEmpleadoVistaOptimizada();
    const list = raw?.results || raw || [];
    exportarExcelDesdeColumnas(
      formatReceived(list),
      columns,
      'Historico_SBC_Empleado',
    );
    setExportandoTodos(false);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form layout="vertical" form={form} name="general">
          <Row gutter={10}>
            <Col xs={24} sm={12}>
              <Form.Item name="empleado" label="No. de Empleado">
                <Select
                  keyProp="id"
                  labelProp="numero_y_nombre_completo"
                  dataSource={empleados}
                  onChange={onChangeEmpleado}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label=" ">
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exportandoTodos}
                  onClick={onClickExportarTodos}
                >
                  Exportar Todos
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          cols={columns}
          data={data}
          allowExportPDF={false}
          permission={permission}
          allowCustomImport
          customImportMenu={menuImport}
          customImportText="Importar SBC"
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys, rows) => {
              setSelectedKeys(keys);
              setSelectedRecord(rows[0] || null);
            },
            type: 'radio',
          }}
          handleOnRowClick={(record) => {
            setSelectedKeys([record.id]);
            setSelectedRecord(record);
          }}
          controls={{
            onClickAdd: handleAdd,
            onClickEdit: handleEdit,
            onClickDelete: () => setVisibleDelete(true),
          }}
          disableEdit={selectedRecord?.estados_globales_id === ESTATUS_AUTORIZADO}
          customActions={[
            {
              permission: { model: 'historicosbcempleado', action: 'cancelar' },
              onClick: () => { if (selectedRecord) setVisibleConfirmCancelar(true); },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRecord
                || selectedRecord?.estados_globales_id === ESTATUS_CANCELADO,
            },
            {
              permission: { model: 'historicosbcempleado', action: 'autorizar' },
              onClick: () => { if (selectedRecord) setVisibleConfirmAutorizar(true); },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRecord
                || selectedRecord?.estados_globales_id === ESTATUS_AUTORIZADO
                || selectedRecord?.estados_globales_id === ESTATUS_CANCELADO,
            },
            {
              permission: { model: 'historicosbcempleado', action: 'reactivar' },
              onClick: () => { if (selectedRecord) setVisibleConfirmReactivar(true); },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRecord
                || selectedRecord?.estados_globales_id === ESTATUS_ACTIVO,
            },
          ]}
        />

        {/* Modal Add / Edit */}
        <Modal
          open={visibleFormModal}
          title={(
            <FormSubmitControls
              label={selectedRecord ? 'Editar SBC' : 'Agregar SBC'}
              onFinish={onFinishHistorial}
              onCancel={() => {
                setVisibleFormModal(false);
                formHistorial.resetFields();
              }}
              loading={loading}
            />
          )}
          footer={null}
          onCancel={() => {
            setVisibleFormModal(false);
            formHistorial.resetFields();
          }}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          destroyOnClose
        >
          <Form form={formHistorial} layout="vertical" onFinish={onFinishHistorial}>
            <Row gutter={16}>
              <Col xs={24} sm={24}>
                <CurrencyInput
                  name="salario_base_cotizacion_imss"
                  label="Salario Base de Cotización IMSS"
                  rules={[{ required: true, message: 'Requerido' }]}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="fecha_inicial"
                  label="Fecha Inicial"
                  rules={[{ required: true, message: 'Requerido' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="fecha_final" label="Fecha Final (vacío = vigente)">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal Eliminar */}
        <ModalDelete
          open={visibleDelete}
          onCancel={() => setVisibleDelete(false)}
          onDelete={confirmDelete}
          content={`el registro SBC del ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Autorizar */}
        <ModalConfirmation
          open={visibleConfirmAutorizar}
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleConfirmAutorizar(false)}
          contentTitle={`Autorizar el SBC vigente desde ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Cancelar */}
        <ModalConfirmation
          open={visibleConfirmCancelar}
          onFinish={onConfirmCancelar}
          Confirm
          onCancel={() => setVisibleConfirmCancelar(false)}
          contentTitle={`Cancelar el SBC vigente desde ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Reactivar */}
        <ModalConfirmation
          open={visibleConfirmReactivar}
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleConfirmReactivar(false)}
          contentTitle={`Reactivar el SBC vigente desde ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Import Excel */}
        <Modal
          open={visibleModal}
          title={(
            <FormSubmitControls
              label="Importar Histórico SBC Empleados"
              onFinish={onUpload}
              onCancel={closeModal}
              loading={loading}
            />
          )}
          onCancel={closeModal}
          footer={null}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          forceRender
          destroyOnClose
        >
          <Spin tip="Cargando..." spinning={loading}>
            <Form layout="vertical" form={formCarga} name="carga" onFinish={onUpload}>
              <Dragger
                label="Archivo"
                limitMB={30}
                rules={[{ required: true, message: 'El archivo es requerido' }]}
                name="file"
                allowedExtensions={['xlsx', 'xls']}
              />
            </Form>
          </Spin>
        </Modal>

        {/* Modal Progreso Importación */}
        <Modal
          open={isPollingImport}
          title="Importando SBC..."
          footer={null}
          closable={false}
          maskClosable={false}
          centered
        >
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Progress
              type="circle"
              percent={
                progresoImport.total > 0
                  ? Math.round((progresoImport.en_curso / progresoImport.total) * 100)
                  : 0
              }
            />
            <p style={{ marginTop: 16 }}>
              {progresoImport.en_curso}
              {' / '}
              {progresoImport.total}
              {' registros procesados'}
            </p>
          </div>
        </Modal>
      </Spin>
    </Row>
  );
};

export default HistoricoSBCEmpleado;
